
import { motion } from "framer-motion";
import { User, Bot } from "lucide-react";
import { useState } from "react";
import { RoundLabel } from "@/components/RoundLabel";
import { ProximityScore } from "@/components/ProximityScore";
import { UserWordInput } from "@/components/UserWordInput";
import { AIWordDisplay } from "@/components/AIWordDisplay";

interface RoundProps {
  userWord: string;
  aiWord: string;
  aiExplanation?: string;
  proximityScore?: number;
  isRevealed: boolean;
  roundNumber: number;
  isLastRound?: boolean;
  isCurrentRound?: boolean;
  isSubmitting?: boolean;
  isLastChance?: boolean;
  autoFocus?: boolean;
  botIsThinking?: boolean;
  userHasSubmitted?: boolean;
  wordError?: string;
  onWordChange?: (word: string) => void;
  onSubmit?: () => void;
  onClearWordError?: () => void;
}

export const Round = ({
  userWord,
  aiWord,
  aiExplanation,
  proximityScore,
  isRevealed,
  roundNumber,
  isLastRound = false,
  isCurrentRound = false,
  isSubmitting = false,
  isLastChance = false, // We keep this prop in the interface for backward compatibility
  autoFocus = false,
  botIsThinking = false,
  userHasSubmitted = false,
  wordError = "",
  onWordChange,
  onSubmit,
  onClearWordError
}: RoundProps) => {
  // CRITICAL FIX: Allow submission even when bot is thinking
  // Only prevent submission if the user has already submitted
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onSubmit && !userHasSubmitted) {
      console.log("Enter key pressed, submitting word. userHasSubmitted:", userHasSubmitted);
      e.preventDefault();
      onSubmit();
    }
  };
  
  console.log(`Round ${roundNumber}: isCurrentRound=${isCurrentRound}, isSubmitting=${isSubmitting}, botIsThinking=${botIsThinking}, userHasSubmitted=${userHasSubmitted}, wordError=${wordError}`);

  // Remove any animation that might cause scrolling
  return (
    <div className="flex flex-col relative max-w-[100%] mx-auto Round">
      {/* Round label - Now with a horizontal line visualization for proximity */}
      <RoundLabel 
        roundNumber={roundNumber} 
        proximityScore={isRevealed ? proximityScore : undefined}
        isCurrentRound={isCurrentRound}
      />
      
      <div className="grid grid-cols-2 gap-4 relative">
        {/* Human/User box - No overlay or blocking when bot is thinking */}
        <div className={`p-4 bg-card rounded shadow-sm border border-border ${isCurrentRound && !userHasSubmitted ? 'focus-within:border-white' : ''}`}>
          <div className="flex flex-col gap-2 items-center">
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
              <User className="w-4 h-4" />
            </div>
            <div className="flex flex-col w-full items-center">
              <UserWordInput
                word={userWord}
                isCurrentRound={isCurrentRound}
                isSubmitting={isSubmitting}
                autoFocus={autoFocus}
                botIsThinking={botIsThinking}
                userHasSubmitted={userHasSubmitted}
                wordError={wordError}
                onWordChange={onWordChange}
                onKeyPress={handleKeyPress}
                onClearWordError={onClearWordError}
              />
            </div>
          </div>
        </div>
        
        {/* AI box */}
        <div className="p-4 bg-card rounded shadow-sm border border-border">
          <div className="flex flex-col gap-2 items-center">
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
              <Bot className="w-4 h-4" />
            </div>
            <div className="flex justify-center items-center w-full">
              <div className="text-foreground text-center w-full">
                <AIWordDisplay
                  word={aiWord}
                  explanation={aiExplanation}
                  isSubmitting={isSubmitting}
                  isCurrentRound={isCurrentRound}
                  isRevealed={isRevealed}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
